import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useState, useEffect, createContext, useContext, useCallback } from 'react'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ChatListPage from './pages/ChatListPage'
import ChatPage from './pages/ChatPage'
import SearchPage from './pages/SearchPage'
import ProfilePage from './pages/ProfilePage'
import SettingsPage from './pages/SettingsPage'
import CallPage from './pages/CallPage'
import CreateGroupPage from './pages/CreateGroupPage'
import GroupChatPage from './pages/GroupChatPage'
import IncomingCallModal from './components/IncomingCallModal'
import { WSMessageTypes } from './hooks/useWebSocket'
import { WebSocketProvider, useSharedWebSocket } from './contexts/WebSocketContext'

// Auth Context
interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  login: (userId: string, username: string, token: string) => void
  logout: () => void
}

interface User {
  id: string
  username: string
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Theme Context
interface ThemeContextType {
  isDarkMode: boolean
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextType | null>(null)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

// Incoming Call State
interface IncomingCall {
  callerId: string
  callerName?: string
  callType: 'audio' | 'video'
  sdp: string
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('lynkstar_token')
    const storedUser = localStorage.getItem('lynkstar_user')

    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(JSON.parse(storedUser))
      setIsAuthenticated(true)
    }

    // Check system dark mode preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const storedTheme = localStorage.getItem('lynkstar_theme')
    setIsDarkMode(storedTheme === 'dark' || (!storedTheme && prefersDark))
  }, [])

  // Apply dark mode class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('lynkstar_theme', isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  const login = (userId: string, username: string, authToken: string) => {
    const newUser = { id: userId, username }
    setUser(newUser)
    setToken(authToken)
    setIsAuthenticated(true)
    localStorage.setItem('lynkstar_token', authToken)
    localStorage.setItem('lynkstar_user', JSON.stringify(newUser))
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    setIsAuthenticated(false)
    localStorage.removeItem('lynkstar_token')
    localStorage.removeItem('lynkstar_user')
  }

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev)
  }

  const authValue: AuthContextType = {
    isAuthenticated,
    user,
    token,
    login,
    logout,
  }

  const themeValue: ThemeContextType = {
    isDarkMode,
    toggleDarkMode,
  }

  return (
    <AuthContext.Provider value={authValue}>
      <ThemeContext.Provider value={themeValue}>
        <WebSocketProvider token={token}>
          <AppContent />
        </WebSocketProvider>
      </ThemeContext.Provider>
    </AuthContext.Provider>
  )
}

// Separate component to use hooks that require Router context
function AppContent() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { lastMessage, sendMessage, isConnected } = useSharedWebSocket()

  // Incoming call state
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null)

  // Listen for incoming calls
  useEffect(() => {
    if (!lastMessage || !isAuthenticated) return

    const { type, payload } = lastMessage

    if (type === WSMessageTypes.RTC_OFFER) {
      const data = payload?.data as Record<string, unknown> | undefined
      const fromId = payload?.from_id as string

      if (fromId && data?.sdp) {
        console.log('[App] Incoming call from:', fromId)
        setIncomingCall({
          callerId: fromId,
          callType: (data.call_type as 'audio' | 'video') || 'audio',
          sdp: data.sdp as string,
        })
      }
    }
  }, [lastMessage, isAuthenticated])

  // Handle accept call
  const handleAcceptCall = useCallback(() => {
    if (!incomingCall) return

    // Store the SDP for the CallPage to use
    sessionStorage.setItem('incoming_call_sdp', incomingCall.sdp)
    sessionStorage.setItem('incoming_call_type', incomingCall.callType)

    // Navigate to call page
    navigate(`/call/${incomingCall.callerId}?type=${incomingCall.callType}&incoming=true`)

    // Clear incoming call state
    setIncomingCall(null)
  }, [incomingCall, navigate])

  // Handle reject call
  const handleRejectCall = useCallback(() => {
    if (!incomingCall) return

    // Send rejection message
    sendMessage({
      type: 'rtc_reject',
      payload: { target_id: incomingCall.callerId },
    })

    // Clear incoming call state
    setIncomingCall(null)
  }, [incomingCall, sendMessage])

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg">
      {/* WebSocket connection status indicator */}
      {isAuthenticated && (
        <div className={`fixed top-2 right-2 z-40 px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
          isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          {isConnected ? 'Online' : 'Offline'}
        </div>
      )}

      {/* Incoming call modal */}
      {incomingCall && (
        <IncomingCallModal
          callerId={incomingCall.callerId}
          callerName={incomingCall.callerName}
          callType={incomingCall.callType}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/chats"
          element={
            <ProtectedRoute>
              <ChatListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:recipientId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/call/:recipientId"
          element={
            <ProtectedRoute>
              <CallPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/group/create"
          element={
            <ProtectedRoute>
              <CreateGroupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/group/:groupId"
          element={
            <ProtectedRoute>
              <GroupChatPage />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/chats" replace />} />
        <Route path="*" element={<Navigate to="/chats" replace />} />
      </Routes>
    </div>
  )
}

export default App
